<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { useRoute } from 'vue-router';

  import type { Task } from '@stuut/api/models';
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
  } from '@ui/components/alert-dialog';
  import { Checkbox } from '@ui/components/checkbox';
  import { Label } from '@ui/components/label';
  import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
  } from '@ui/components/select';
  import { toast } from '@ui/components/sonner';
  import { Textarea } from '@ui/components/textarea';

  import { stuut } from '@/api';
  import { useTaskCollectionStore, useTaskStore } from '@/store';

  interface Props {
    actionType?: string;
    show?: boolean;
    tasks: Task[];
  }

  const props = defineProps<Props>();

  const emits = defineEmits<{
    (e: 'close'): void;
    (e: 'complete'): void;
    (e: 'clearSelection'): void;
  }>();

  const generalOptions = [
    'Resolved outside platform',
    'No action needed',
    'Invoice was paid',
    'No longer relevant',
    'Duplicate task',
    'Other',
  ];

  const qualityOptions = [
    'Analysis or classification issue',
    'Unsatisfactory recommendation',
    'Task not necessary',
    'Duplicate task',
    'Other',
  ];

  const closeReason = ref<string | undefined>();
  const closeReasonNote = ref<string | undefined>();
  const isSubmitting = ref<boolean>(false);
  const clearDateOnDismiss = ref(true);
  const isOpen = computed(() => props.show);
  const taskStore = useTaskStore();
  const route = useRoute();
  const taskCollectionStore = useTaskCollectionStore();

  const hasPaymentPromiseTask = computed(() =>
    Boolean(props.tasks?.some((t) => t?.isPaymentPromise)),
  );

  const dismissData = computed(() =>
    hasPaymentPromiseTask.value ? { clear_promise: clearDateOnDismiss.value } : undefined,
  );

  watch(
    () => props.show,
    (show) => {
      if (show) clearDateOnDismiss.value = true;
    },
  );
  const AlertTypePropMap = {
    close: {
      description: 'Are you sure you want to close this task?',
      options: generalOptions,
      placeholder: "Optional: leave a note why you're closing this task",
      title: 'Dismiss task',
    },
    mark_inaccurate: {
      description: 'Are you sure you want to close this task and flag it inaccurate?',
      options: qualityOptions,
      placeholder:
        'Optional: Let us know why this task is inaccurate so we can improve future tasks.',
      title: 'Mark inaccurate task',
    },
  };

  const alertTypeProps = computed(() => {
    if (!props.actionType) return {};
    return AlertTypePropMap?.[props.actionType];
  });

  const ACTION_TYPES = {
    close: 'close',
    mark_inaccurate: 'mark_inaccurate',
  };

  const SUCCESS_MESSAGES = {
    [ACTION_TYPES.close]: 'Tasks bulk closed.',
    [ACTION_TYPES.mark_inaccurate]: 'Tasks bulk marked inaccurate.',
  };

  const performTaskAction = async (actionType: string) => {
    if (isSubmitting.value) return;

    isSubmitting.value = true;

    emits('close');

    try {
      const taskIds = props.tasks?.map?.((task) => task.id);

      const response = await stuut.tasks.bulkAction(
        taskIds,
        actionType,
        closeReason.value,
        closeReasonNote.value,
        props.tasks,
        dismissData.value,
      );

      toast.success(SUCCESS_MESSAGES[actionType]);
      emits('clearSelection');

      taskCollectionStore.afterCompleteTask();
      await taskCollectionStore.updateFromQps(route);
      taskCollectionStore?.refreshAllTaskTables();

      return response;
    } catch (err: any) {
      toast.error(err.clientMessage || 'An error occurred');
    } finally {
      isSubmitting.value = false;
      emits('complete');
    }
  };

  const closeTask = () => performTaskAction(ACTION_TYPES.close);
  const markInaccurateTask = () => performTaskAction(ACTION_TYPES.mark_inaccurate);

  const onClose = () => {
    closeReason.value = undefined;
    closeReasonNote.value = undefined;

    emits('close');
  };

  const onConfirm = async () => {
    switch (props.actionType) {
      case 'close':
        return closeTask();
      case 'mark_inaccurate':
        return markInaccurateTask();
      default:
        return undefined;
    }
  };
</script>

<template>
  <AlertDialog v-model:open="isOpen">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{{ alertTypeProps?.title }}</AlertDialogTitle>
        <AlertDialogDescription as="div" class="flex flex-col gap-y-4">
          <span v-if="alertTypeProps?.description">{{ alertTypeProps?.description }}</span>

          <Label
            v-if="hasPaymentPromiseTask"
            class="flex items-center gap-x-2 px-1 w-fit text-xs font-semibold"
          >
            <Checkbox
              :model-value="clearDateOnDismiss"
              @update:model-value="clearDateOnDismiss = Boolean($event)"
            />
            <span>Clear promise to pay dates</span>
          </Label>

          <Select
            :model-value="closeReason"
            class="border-border"
            @update:model-value="(event) => (closeReason = event)"
          >
            <SelectTrigger class="w-full border-border" clearable>
              <SelectValue placeholder="Select reason" />
            </SelectTrigger>

            <SelectContent>
              <SelectItem v-for="option in alertTypeProps.options" :key="option" :value="option">
                {{ option }}
              </SelectItem>
            </SelectContent>
          </Select>

          <Textarea
            v-if="closeReason"
            v-model="closeReasonNote"
            class="w-full border border-border text-xs"
            :placeholder="alertTypeProps?.placeholder"
          />
        </AlertDialogDescription>
      </AlertDialogHeader>

      <AlertDialogFooter>
        <AlertDialogCancel @click="onClose">Cancel</AlertDialogCancel>
        <AlertDialogAction @click="onConfirm">Dismiss</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>

<style scoped></style>
